import i18next from 'i18next';
import { LOCAL_STORAGE_IDS } from 'Shared/constants';
import { UPMUserAnswer } from 'Shared/models/swagger/upm';
import { getLocalStorage, setLocalStorage } from 'Shared/services/localStorageService';
import { IDPUserResponseInternal } from 'Shared/services/userActivation/api/consts';

import { COUNTRY_STATE_MAP } from './countryStateMap';

export interface LeaderBoardPointsCache {
  totalPoints: number;
  updatedAt  : Date | null;
}

export interface LeaderBoardUser {
  id           : number;
  initials     : string;
  points       : number;
  name         : string;
  isCurrentUser: boolean;
}

interface UserAnswers {
  userAnswers: UPMUserAnswer[];
}

export interface CountryState {
  country: string;
  state  : string;
}


export enum LEADER_BOARD_STYLE {
  FIRST,
  TOP_THREE,
  TOP_EIGHT,
  TOP_TWENTY,
  TOP_TWO_HUNDRED,
  NOT_RANKED
}

export type LeaderBoardContent = {
  users: LeaderBoardUser[];
  style: LEADER_BOARD_STYLE;
}

export type LeaderBoardCache = {
  updatedAt: Date | null;
  data     : User[];
}

const USERS_AMOUNT_DEFAULT           = 8;
const USERS_AMOUNT_DEFAULT_MIN_INDEX = 2;
const USERS_AMOUNT_DEFAULT_MAX_INDEX = 4;

const USERS_AMOUNT_TWENTY_MIN_INDEX = 15;
const USERS_AMOUNT_TWENTY_MAX_INDEX = 17;

export const LEADERBOARD_SIGNUP_DURATION_DAYS = 5;

/**
 * When user ranking is TOP_TWO_HUNDRED, show first 3 users skip two and show last three users
 * @param index
 * @param userRanking
 * @param usersAmountToShow
 * @returns
 */
export const skipRankedUser = (index: number, userRanking: LEADER_BOARD_STYLE, usersAmountToShow = USERS_AMOUNT_DEFAULT): boolean => {
  const areEightUsers = usersAmountToShow === USERS_AMOUNT_DEFAULT;
  const minIndex = areEightUsers ? USERS_AMOUNT_DEFAULT_MIN_INDEX : USERS_AMOUNT_TWENTY_MIN_INDEX;
  const maxIndex = areEightUsers ? USERS_AMOUNT_DEFAULT_MAX_INDEX : USERS_AMOUNT_TWENTY_MAX_INDEX;

  if (areEightUsers && userRanking === LEADER_BOARD_STYLE.TOP_TWENTY && (index > minIndex && index < maxIndex)) {
    return true;
  }

  if (userRanking === LEADER_BOARD_STYLE.TOP_TWO_HUNDRED && (index > minIndex && index < maxIndex)) {
    return true;
  }

  return false;
};

export type User = {
  firstName: string;
  lastName : string;
  points   : string;
};

/**
 * Generates initials of the user
 * @param firstName
 * @param lastName
 * @returns
 */
export function getInitials(firstName: string, lastName: string): string {
  return `${firstName.charAt(0).toUpperCase()}${lastName.charAt(0).toUpperCase()}`;
}
/**
 * Upper case the first letter of the first name
 */
export const validateFirstName = (firstName: string): string => {
  return firstName.charAt(0).toUpperCase() + firstName.slice(1);
};

/**
 * Format user data for leaderboard component
 * @param id
 * @param user
 * @param isCurrentUser
 * @returns
 */
export function formatUser(id: number, user: User, isCurrentUser: boolean): LeaderBoardUser {
  return {
    id,
    initials: getInitials(user.firstName, user.lastName),
    name    : isCurrentUser ? i18next.t('leaderBoard:LeaderBoard--currentUser'): `${validateFirstName(user.firstName)} ${user.lastName.charAt(0).toUpperCase()}.`,
    isCurrentUser,
    points  : parseInt(user.points)
  };
}

/**
 * Returns the style of the leaderboard based on the current user's rank
 * @param users
 * @returns
 */
const getLeaderBoardStyle = (users: LeaderBoardUser[]): LEADER_BOARD_STYLE => {
  const currentUser = users.find(user => user.isCurrentUser);
  const currentUserRank = currentUser?.id || 0;

  if (currentUserRank === 0) {
    return LEADER_BOARD_STYLE.NOT_RANKED;
  }

  if (currentUserRank === 1) {
    return LEADER_BOARD_STYLE.FIRST;
  }

  if (currentUserRank <= 3) {
    return LEADER_BOARD_STYLE.TOP_THREE;
  }

  if (currentUserRank <= 8) {
    return LEADER_BOARD_STYLE.TOP_EIGHT;
  }

  if (currentUserRank <= 20) {
    return LEADER_BOARD_STYLE.TOP_TWENTY;
  }

  return LEADER_BOARD_STYLE.TOP_TWO_HUNDRED;
};

/**
 * Process users data received from the daily cron and returns formatted data for leaderboard component
 * @param users
 * @param userPoints
 * @param currentUser
 * @param numberOfUsersToShow
 * @returns
 */
export function getLeaderBoardContent(
  users              : User[],
  userPoints         : number | undefined,
  currentUser        : IDPUserResponseInternal | null | undefined,
  numberOfUsersToShow = 8
): LeaderBoardContent {
  const OUTSIDE_TOP_200 = 201;

  if (!Array.isArray(users) || users.length === 0 || !currentUser || users.length < numberOfUsersToShow || userPoints === undefined) {
    return {
      users: [],
      style: LEADER_BOARD_STYLE.NOT_RANKED
    };
  }

  // format current user data
  const currentUserData: User = {
    firstName: currentUser?.firstName || '',
    lastName : currentUser?.lastName || '',
    points   : userPoints.toString() || '0'
  };

  // parse points and sort users by points in descending order
  const sortedUsers = users.sort((a, b) => parseInt(b.points) - parseInt(a.points));

  // format all users for leaderboard component needs
  const formattedUsers    = sortedUsers.map((user, index) => formatUser(index + 1, user, false));
  const currentUserPoints = userPoints || 0;

  // find the index/current user rank in sorted list
  let currentUserRank        = formattedUsers.findIndex(user => user.points <= currentUserPoints);
  currentUserRank        = currentUserRank === -1 ? OUTSIDE_TOP_200 : currentUserRank;
  const currentUserFormatted = formatUser(currentUserRank, currentUserData, true);

  // if current user is in the top X (where X is numberOfUsersToShow)
  if (currentUserRank >= 0 && currentUserRank < numberOfUsersToShow) {
    formattedUsers.splice(currentUserRank, 0, currentUserFormatted);
    const topUsers = formattedUsers.slice(0, numberOfUsersToShow).map((user, index) => ({
      ...user,
      id: index + 1
    }));

    return {
      users: topUsers,
      style: getLeaderBoardStyle(topUsers)
    };
  }

  // if current user is in the top 200, grab the top X users, current user and preceding 2 users
  if (currentUserRank < 200) {
    const areEightUsers    = numberOfUsersToShow === 8;
    const sliceAmount      = areEightUsers ? 5 : 18;
    const topUsers         = formattedUsers.slice(0, sliceAmount);
    const precedingTwo     = formattedUsers.slice(Math.max(0, currentUserRank - 2), currentUserRank);
    const topUsersCombined = [...topUsers, ...precedingTwo, currentUserFormatted];

    const reRankedTopUsers = topUsersCombined.map((user) => {
      if (user.isCurrentUser) {
        return {
          ...user,
          id: currentUserRank + 1 // set the correct rank for the current user
        };
      }
      return user;
    });

    if (currentUserRank < numberOfUsersToShow) {
      reRankedTopUsers.splice(sliceAmount - 2, 1); // remove the Xth user (the one that is not shown in the leaderboard))
    } else {
      reRankedTopUsers.splice(sliceAmount - 1, 1); // remove the Xth user (the one that is not shown in the leaderboard))
    }


    return {
      users: reRankedTopUsers,
      style: getLeaderBoardStyle(reRankedTopUsers)
    };
  }

  // if user is not in the top 200
  const topUsers = formattedUsers.slice(0, numberOfUsersToShow).map((user, index) => ({
    ...user,
    id: index + 1
  }));

  return {
    users: topUsers,
    style: LEADER_BOARD_STYLE.NOT_RANKED
  };
}

/**
 * Get the country and state from the user answers
 * @param data
 * @returns
 */
export function getCountryAndState(data: UserAnswers): CountryState {
  const userAnswers = data.userAnswers || [];
  const result: CountryState = {
    country: "",
    state  : ""
  };

  userAnswers.forEach(answer => {
    if (answer.questionKey === "GeoCountry") {
      result.country = answer.answerValue as string;
    }
    if (answer.questionKey === "GeoRegion") {
      result.state = answer.answerValue as string;
    }
  });

  return result;
}

/**
 * Returns the user's state based on the country and state abbreviation
 * @param country
 * @param state
 * @returns
 */
export const getUserState = (country?: string, state?: string): string => {
  let stateName = i18next.t('leaderBoard:LeaderBoard--default-area');

  if (!country || !state) {
    return stateName;
  }

  const countryStates = COUNTRY_STATE_MAP[country.toLowerCase()];

  if (!countryStates) {
    return stateName;
  }

  stateName = countryStates[state.toLowerCase()] || stateName;

  return stateName;
};

/**
 * Sets the leaderboard data in local storage
 * @param leaderBoardData
 */
export const setLocalLeaderBoardCache = (leaderBoardData: User[]): void => {
  const updatedAt = new Date();

  setLocalStorage(LOCAL_STORAGE_IDS.LEADER_BOARD_DATA, {
    updatedAt: updatedAt,
    data: leaderBoardData
  }, true);
};

/**
 * Retrieves the cached leaderboard data
 * @returns
 */
export const getLeaderboardCachedData = (): LeaderBoardCache => {
  const leaderboardCache = getLocalStorage(LOCAL_STORAGE_IDS.LEADER_BOARD_DATA, true);

  if (!leaderboardCache || !leaderboardCache.updatedAt || !Array.isArray(leaderboardCache.data)) {
    return {
      updatedAt: null,
      data: [],
    };
  }

  leaderboardCache.updatedAt = new Date(leaderboardCache.updatedAt);

  return leaderboardCache;
};

/**
 * Determines if the cache is from today
 * @param cacheDate
 * @returns
 */
export const isCacheFromToday = (cacheDate: Date | null): boolean => {
  if (!cacheDate) {
    return false;
  }

  const today = new Date();

  return (
    cacheDate.getFullYear() === today.getFullYear() &&
    cacheDate.getMonth() === today.getMonth() &&
    cacheDate.getDate() === today.getDate()
  );
};

